import { toolLabelProviderFor } from '@platform/sem/toolLabels';
import { Chip, Collapse, Icon, IconButton, LinearProgress, Paper, Typography } from '@shared/ui';
import { deepMerge } from '@shared/utils/merge';
import { type KeyboardEventHandler, useEffect, useState } from 'react';
import { registerWidgetRenderer } from '../registry';
import { iconForTool } from '../toolIcons';
import type { DeepPartial, TimelineWidgetProps, ToolCallEntity, ToolWidgetStyles } from '../types';
import { DriveDocumentToolResultWidget } from './DriveDocumentToolResultWidget';
import { hasUsableDriveContent } from './driveContent';

export interface ToolWidgetProps extends TimelineWidgetProps<ToolCallEntity> {
  debugMode?: boolean;
  toolWidgetStyles?: DeepPartial<ToolWidgetStyles>;
}

export function ToolWidget({ entity, debugMode = false, toolWidgetStyles }: ToolWidgetProps) {
  const {
    name,
    status = 'queued',
    progress,
    input,
    label,
    argsSummary,
    resultSummary,
    resultCount,
    result,
    error,
    details,
  } = entity.props as ToolCallEntity['props'];
  const [expanded, setExpanded] = useState(debugMode);
  const driveData = (entity.props as any)?.driveData as any | undefined;
  const hasEmbeddedDriveResult = name === 'drive_fetch_document_content' && !!driveData;
  const isDriveContentUnavailable =
    hasEmbeddedDriveResult &&
    (driveData?.status === 'content_unavailable' ||
      (driveData?.status !== 'success' && !hasUsableDriveContent(driveData)));
  const effectiveStatus = isDriveContentUnavailable ? 'error' : status;

  useEffect(() => {
    if (debugMode) setExpanded(true);
  }, [debugMode]);

  // Default styles if not provided - restoring original hardcoded styles as defaults
  const defaultStyles: ToolWidgetStyles = {
    queued: {
      backgroundColor: '', // Will use className bg-blue-50
      borderColor: '', // Not used in original
      textColor: 'text.secondary', // Original uses color="text.secondary" for icons/text
      progressBarBgColor: '', // Not used in original
      progressBarColor: '', // Not used in original
      codeBlockBgColor: 'white', // Original uses bg-white
    },
    running: {
      backgroundColor: '', // Will use className bg-blue-50
      borderColor: '',
      textColor: 'text.secondary',
      progressBarBgColor: '',
      progressBarColor: '',
      codeBlockBgColor: 'white',
    },
    completed: {
      backgroundColor: '', // Will use className bg-green-50
      borderColor: '',
      textColor: 'text.secondary',
      progressBarBgColor: '',
      progressBarColor: '',
      codeBlockBgColor: 'white',
    },
    error: {
      backgroundColor: '', // Will use className bg-red-50
      borderColor: '',
      textColor: 'text.secondary', // Original error text uses color="error", others use text.secondary
      progressBarBgColor: '',
      progressBarColor: '',
      codeBlockBgColor: 'white',
    },
  };

  const styles = deepMerge(defaultStyles, toolWidgetStyles);
  const statusStyles = styles[effectiveStatus] || styles.queued;

  const statusColors = {
    queued: 'default' as const,
    running: 'primary' as const,
    completed: 'success' as const,
    error: 'error' as const,
  };
  const statusIcons = {
    queued: 'schedule',
    running: 'play_circle',
    completed: 'check_circle',
    error: 'error',
  } as const;

  const toggleExpanded = () => setExpanded((prev) => !prev);
  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleExpanded();
    }
  };

  const hasProgress = typeof progress === 'number';
  const pct = hasProgress ? Math.round((progress as number) * 100) : undefined;

  // Fallback to provider-derived labels if not present on the entity
  const provider = toolLabelProviderFor(name);
  const derived = provider.labelForStart(name, (input as any) || {});
  const titleText =
    effectiveStatus === 'running'
      ? (label || derived.label || name) + '…'
      : label || derived.label || `🔧 ${name}`;
  const driveName =
    hasEmbeddedDriveResult && typeof (driveData as any)?.name === 'string'
      ? String((driveData as any).name)
      : undefined;
  // For drive fetch, avoid showing raw IDs; prefer the fetched document name when available.
  const searchTerms =
    name === 'drive_fetch_document_content' ? driveName : argsSummary || derived.argsSummary;

  // Calculate background class based on status if no explicit background color is provided in styles
  const defaultBgClass =
    effectiveStatus === 'completed'
      ? 'bg-green-50'
      : effectiveStatus === 'error'
        ? 'bg-red-50'
        : 'bg-blue-50';

  return (
    <Paper
      variant="outlined"
      className={`p-3 w-full min-w-0 ${!statusStyles.backgroundColor ? defaultBgClass : ''} mp-block artifact-content`}
      sx={{
        bgcolor: statusStyles.backgroundColor || undefined,
        borderColor: statusStyles.borderColor || undefined,
        color: statusStyles.textColor,
      }}
    >
      <div className="flex items-start gap-2 min-w-0">
        <Icon sx={{ fontSize: 20, color: statusStyles.textColor }}>
          {statusIcons[effectiveStatus]}
        </Icon>
        <div className="flex-1 min-w-0">
          <div
            className="flex items-center gap-2 cursor-pointer min-w-0"
            role="button"
            tabIndex={0}
            onClick={toggleExpanded}
            onKeyDown={handleKeyDown}
            aria-expanded={expanded}
            aria-label="Toggle tool details"
          >
            <Icon sx={{ fontSize: 18, color: statusStyles.textColor }}>{iconForTool(name)}</Icon>
            <Typography
              variant="subtitle2"
              className="min-w-0 flex-shrink-0"
              sx={{ color: statusStyles.textColor }}
            >
              {titleText}
            </Typography>
            {searchTerms ? (
              <Typography
                variant="caption"
                className="truncate max-w-[240px]"
                sx={{ color: statusStyles.textColor }}
              >
                {searchTerms}
              </Typography>
            ) : null}
            <Chip
              label={effectiveStatus}
              size="small"
              color={statusColors[effectiveStatus]}
              className="flex-shrink-0"
              sx={{
                borderColor: statusStyles.borderColor || undefined,
                color:
                  statusStyles.textColor === 'text.secondary' ? undefined : statusStyles.textColor,
              }}
            />
            <IconButton
              size="small"
              sx={{ ml: 'auto', flexShrink: 0, color: statusStyles.textColor }}
            >
              <Icon sx={{ fontSize: 18 }}>{expanded ? 'expand_less' : 'expand_more'}</Icon>
            </IconButton>
          </div>

          {effectiveStatus === 'running' && hasProgress && (
            <div className="flex items-center gap-2 mt-2">
              <LinearProgress
                variant="determinate"
                value={pct}
                style={{ flexGrow: 1 }}
                sx={{
                  bgcolor: statusStyles.progressBarBgColor || undefined,
                  '& .MuiLinearProgress-bar': {
                    bgcolor:
                      statusStyles.progressBarColor ||
                      (statusStyles.textColor !== 'text.secondary'
                        ? statusStyles.textColor
                        : undefined),
                  },
                }}
              />
              <Typography variant="caption" sx={{ color: statusStyles.textColor }}>
                {pct}%
              </Typography>
            </div>
          )}

          <Collapse in={expanded}>
            {/* Specialized view for Drive document fetch results */}
            {hasEmbeddedDriveResult ? (
              <div className="mt-2 min-w-0">
                <DriveDocumentToolResultWidget
                  entity={
                    {
                      id: `${entity.id}-drive`,
                      kind: 'tool_result',
                      timestamp: entity.timestamp,
                      props: { toolCallId: entity.id, result: driveData },
                    } as any
                  }
                  debugMode={debugMode}
                  embedded
                />
              </div>
            ) : null}
            {/* Backend-provided details (preferred) */}
            {Array.isArray(details) && details.length > 0 ? (
              <div className="mt-2 min-w-0 space-y-2">
                {details.map((block: any, idx: number) => (
                  <div key={idx} className="min-w-0">
                    {block?.title ? (
                      <Typography
                        variant="caption"
                        className="block mb-1"
                        sx={{ color: statusStyles.textColor }}
                      >
                        {block.title}
                      </Typography>
                    ) : null}
                    {Array.isArray(block?.kv) && block.kv.length > 0 ? (
                      <div className="space-y-1">
                        {block.kv.map((row: any, i: number) => (
                          <div key={i} className="flex items-start gap-2 min-w-0">
                            <Typography
                              variant="caption"
                              className="flex-shrink-0"
                              sx={{ color: statusStyles.textColor }}
                            >
                              {String(row?.key)}:
                            </Typography>
                            <Typography
                              variant="body2"
                              className="truncate"
                              sx={{ color: statusStyles.textColor }}
                            >
                              {String(row?.value)}
                            </Typography>
                          </div>
                        ))}
                      </div>
                    ) : null}
                    {block?.text ? (
                      <Typography variant="body2" sx={{ color: statusStyles.textColor }}>
                        {String(block.text)}
                      </Typography>
                    ) : null}
                    {block?.json ? (
                      <div className="overflow-x-auto max-w-full">
                        <pre
                          className="text-xs rounded p-2 overflow-x-auto max-h-32 whitespace-pre"
                          style={{
                            backgroundColor:
                              statusStyles.codeBlockBgColor === 'white'
                                ? 'white'
                                : statusStyles.codeBlockBgColor || 'rgba(189, 204, 190, 0.1)',
                            color: statusStyles.textColor,
                            border: statusStyles.borderColor
                              ? `1px solid ${statusStyles.borderColor}`
                              : undefined,
                          }}
                        >
                          {JSON.stringify(block.json, null, 2)}
                        </pre>
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}

            {/* Fallbacks for older events/providers */}
            {!hasEmbeddedDriveResult && !details && searchTerms ? (
              <div className="mt-2 min-w-0">
                <Typography
                  variant="caption"
                  className="block mb-1"
                  sx={{ color: statusStyles.textColor }}
                >
                  Search terms:
                </Typography>
                <Typography variant="body2" sx={{ color: statusStyles.textColor }}>
                  {searchTerms}
                </Typography>
              </div>
            ) : null}

            {(debugMode || (!hasEmbeddedDriveResult && !details && !searchTerms)) && input ? (
              <div className="mt-2 min-w-0">
                <Typography
                  variant="caption"
                  className="block mb-1"
                  sx={{ color: statusStyles.textColor }}
                >
                  Input:
                </Typography>
                <div className="overflow-x-auto max-w-full">
                  <pre
                    className="text-xs rounded p-2 overflow-x-auto max-h-32 whitespace-pre"
                    style={{
                      backgroundColor:
                        statusStyles.codeBlockBgColor === 'white'
                          ? 'white'
                          : statusStyles.codeBlockBgColor || 'rgba(189, 204, 190, 0.1)',
                      color: statusStyles.textColor,
                      border: statusStyles.borderColor
                        ? `1px solid ${statusStyles.borderColor}`
                        : undefined,
                    }}
                  >
                    {JSON.stringify(input, null, 2)}
                  </pre>
                </div>
              </div>
            ) : null}

            {typeof resultCount === 'number' || resultSummary ? (
              <div className="mt-2 min-w-0">
                <Typography
                  variant="caption"
                  className="block mb-1"
                  sx={{ color: statusStyles.textColor }}
                >
                  Result:
                </Typography>
                <Typography variant="body2" sx={{ color: statusStyles.textColor }}>
                  {resultSummary ?? ''}
                  {typeof resultCount === 'number' ? ` (${resultCount})` : ''}
                </Typography>
              </div>
            ) : null}

            {debugMode && result ? (
              <div className="mt-2 min-w-0">
                <Typography
                  variant="caption"
                  className="block mb-1"
                  sx={{ color: statusStyles.textColor }}
                >
                  Raw Result:
                </Typography>
                <div className="overflow-x-auto max-w-full">
                  <pre
                    className="text-xs rounded p-2 overflow-x-auto max-h-64 whitespace-pre"
                    style={{
                      backgroundColor:
                        statusStyles.codeBlockBgColor === 'white'
                          ? 'white'
                          : statusStyles.codeBlockBgColor || 'rgba(189, 204, 190, 0.1)',
                      color: statusStyles.textColor,
                      border: statusStyles.borderColor
                        ? `1px solid ${statusStyles.borderColor}`
                        : undefined,
                    }}
                  >
                    {JSON.stringify(result, null, 2)}
                  </pre>
                </div>
              </div>
            ) : null}

            {error ? (
              <div className="mt-2 min-w-0">
                <Typography
                  variant="body2"
                  className="break-words"
                  sx={{
                    color:
                      effectiveStatus === 'error' && statusStyles.textColor === 'text.secondary'
                        ? 'error.main'
                        : statusStyles.textColor,
                  }}
                >
                  {error}
                </Typography>
              </div>
            ) : null}
          </Collapse>
        </div>
      </div>
    </Paper>
  );
}

// Register unified tool widget for normal and debug modes
registerWidgetRenderer(
  'tool_call',
  ({ entity, debugMode, toolWidgetStyles }) => (
    <ToolWidget
      key={(entity as any).id}
      entity={entity as any}
      debugMode={debugMode}
      toolWidgetStyles={toolWidgetStyles}
    />
  ),
  { visibility: { normal: true, debug: true } }
);
